import { useCallback, useEffect, useRef } from 'react'
import type { NavigateFunction } from 'react-router-dom'
import type { ToastSeverity } from '@/shared/toast/ToastProvider'
import { notifyInstrumentUnavailable, notifySurveySessionTimedOut } from './handle-survey-session-blocked'
import { INSTRUMENT_UNAVAILABLE } from './instrument-info-messages'
import {
  resolveSurveySaveFailure,
  sessionTargetBlockedFromResponse,
  sessionTimeoutFromResponse,
} from './survey-save-error'
import type { ProviderResponseState } from './types'

type ShowToast = (message: string, severity?: ToastSeverity, autoHideDuration?: number) => void

export function instrumentUnavailableFromResponse(response: ProviderResponseState): boolean {
  return (
    response.status === 'abandoned_instrument_unavailable' ||
    response.terminationReason === 'abandoned_instrument_unavailable'
  )
}

export function useSurveyExecutionSessionGuards(
  response: ProviderResponseState | undefined,
  showToast: ShowToast,
  navigate: NavigateFunction,
  setTargetBlocked: (blocked: boolean) => void,
) {
  const timeoutHandledRef = useRef(false)
  const unavailableHandledRef = useRef(false)

  const handleSessionFailure = useCallback(
    (failure: ReturnType<typeof resolveSurveySaveFailure>) => {
      if (failure.kind === 'instrument_paused') {
        if (unavailableHandledRef.current) return
        unavailableHandledRef.current = true
        notifyInstrumentUnavailable(showToast, navigate, failure.message)
        return
      }
      if (failure.kind === 'session_timeout') {
        if (timeoutHandledRef.current) return
        timeoutHandledRef.current = true
        notifySurveySessionTimedOut(showToast, navigate, failure.message)
        return
      }
      if (failure.kind === 'session_target_blocked') {
        setTargetBlocked(true)
      }
    },
    [navigate, showToast, setTargetBlocked],
  )

  useEffect(() => {
    if (!response || timeoutHandledRef.current || unavailableHandledRef.current) return
    if (instrumentUnavailableFromResponse(response)) {
      unavailableHandledRef.current = true
      notifyInstrumentUnavailable(showToast, navigate, INSTRUMENT_UNAVAILABLE)
      return
    }
    if (sessionTimeoutFromResponse(response)) {
      timeoutHandledRef.current = true
      notifySurveySessionTimedOut(showToast, navigate)
      return
    }
    if (sessionTargetBlockedFromResponse(response)) {
      setTargetBlocked(true)
    }
  }, [response, navigate, showToast, setTargetBlocked])

  useEffect(() => {
    if (!response?.instrumentTargetReached || timeoutHandledRef.current) return
    setTargetBlocked(true)
  }, [response?.instrumentTargetReached, setTargetBlocked])

  return { timeoutHandledRef, handleSessionFailure }
}
